import styled from '@emotion/styled';
import { companyData, countryAndRegions } from '@gameonly/core';
import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import TextField from '../project/parameters/TextField';
import BaseBlock from '../ui/Block';
import LoadingButton from '../ui/LoadingButton';
import useUser from './context/useUser';
import { alphabetically, putAutreAtTheEnd } from './Register';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: 300px;
`;

const Block = styled(BaseBlock)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 32px 38px;
  width: 100%;
`;

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Entrez un email valide')
    .required('Champ obligatoire'),
  country: yup.string().required('Champ obligatoire'),
  firstName: yup.string().required('Champ obligatoire'),
  lastName: yup.string().required('Champ obligatoire'),
  company: yup.string().required('Champ obligatoire'),
  companyType: yup.string().required('Champ obligatoire'),
  region: yup.string().when('country', {
    is: (country: string) =>
      country &&
      countryAndRegions[country as keyof typeof countryAndRegions]?.length > 0,
    then: yup
      .string()
      .required('Champ obligatoire pour les régions de ce pays'),
  }),
});

interface ProfileChangeModalProps {
  open: boolean;
  handleClose: () => void;
}

const ProfileChangeModal = ({ open, handleClose }: ProfileChangeModalProps) => {
  const { t } = useTranslation(['authentication', 'common']);
  const { user, loading, error, changeProfile } = useUser();
  const { enqueueSnackbar } = useSnackbar();

  const [showRegion, setShowRegion] = useState(false);

  const studios = Object.keys(companyData);

  const formik = useFormik({
    initialValues: {
      email: user?.email,
      firstName: user?.firstName,
      lastName: user?.lastName,
      company: user?.company,
      companyType: user?.companyType || '',
      country: user?.country || '',
      region: user?.region || '',
      news: user?.news || false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await changeProfile({
        email: values.email || '',
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        company: values.company || '',
        companyType: values.companyType || '',
        country: values.country || '',
        region: values.region || '',
        news: values?.news || false,
      });
      enqueueSnackbar('Profil mis à jour avec succès', {
        variant: 'success',
      });
      onClose();
    },
  });

  useEffect(() => {
    formik.setValues({ ...formik.values, ...user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const isRegion =
      formik.values.country.length > 0 &&
      countryAndRegions[formik.values.country as keyof typeof countryAndRegions]
        ?.length > 0;
    setShowRegion(isRegion);
    if (!isRegion) {
      formik.setFieldValue('region', '');
    }
  }, [formik.values.country, countryAndRegions]);

  useEffect(() => {
    const studio = formik.values.company;
    if (studio && studios.includes(studio)) {
      const selectedCompanyType =
        companyData[studio as keyof typeof companyData];
      formik.setFieldValue('companyType', selectedCompanyType);
    }
  }, [formik.values.company, companyData]);

  const onClose = () => {
    formik.resetForm();
    handleClose();
  };

  const companyTypes = [...new Set(Object.values(companyData))]
    .map((companyType) => ({
      key: companyType,
      label: t(`authentication:register.company-types.${companyType}`),
    }))
    .sort(alphabetically)
    .sort(putAutreAtTheEnd);

  const countries = Object.keys(countryAndRegions)
    .map((key) => ({
      key,
      label: t(`common:countries.${key}`),
    }))
    .sort(alphabetically)
    .sort(putAutreAtTheEnd);

  const regions = showRegion
    ? Object.values(
        countryAndRegions[
          formik.values.country as keyof typeof countryAndRegions
        ] || {},
      ).map((key) => ({
        key,
        label: t(`common:regions.${key}`),
      }))
    : [];

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle id="scroll-dialog-title">
          {t('edit-profile-modal.title')}
        </DialogTitle>
        <DialogContent dividers={true}>
          <Container>
            {!!error && <Block accent>{`${error}`}</Block>}
            <TextField
              id="email"
              name="email"
              label={t('edit-profile-modal.mail-address')}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              disabled={true}
              fullWidth
            />
            <TextField
              id="firstName"
              name="firstName"
              label={t('edit-profile-modal.first-name')}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
              disabled={loading}
              fullWidth
            />
            <TextField
              id="lastName"
              name="lastName"
              label={t('edit-profile-modal.last-name')}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              disabled={loading}
              fullWidth
            />
            <Autocomplete
              id="company"
              fullWidth
              freeSolo
              options={studios}
              value={formik.values.company}
              onBlur={(event) => {
                // onChange not triggered after a value selection followed by its edit and finally blur
                formik.setFieldValue(
                  'company',
                  (event.target as HTMLInputElement).value || '',
                );
              }}
              onChange={(event, newValue) => {
                formik.setFieldValue('company', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                formik.setFieldValue('company', newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('edit-profile-modal.company-name')}
                  error={
                    formik.touched.company && Boolean(formik.errors.company)
                  }
                  helperText={formik.touched.company && formik.errors.company}
                  disabled={loading}
                  fullWidth
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel id="companyType-label">
                {t('edit-profile-modal.company-type')}
              </InputLabel>
              <Select
                labelId="companyType-label"
                id="companyType"
                label={t('edit-profile-modal.company-type')}
                name="companyType"
                value={formik.values.companyType}
                onChange={formik.handleChange}
                error={
                  formik.touched.companyType &&
                  Boolean(formik.errors.companyType)
                }
                disabled={loading}
                fullWidth
              >
                {companyTypes.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="country-label">
                {t('edit-profile-modal.country')}
              </InputLabel>
              <Select
                id="country"
                labelId="country-label"
                label={t('edit-profile-modal.country')}
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                disabled={loading}
                fullWidth
              >
                {countries.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {showRegion && (
              <FormControl fullWidth>
                <InputLabel id="region-label">
                  {t('edit-profile-modal.region')}
                </InputLabel>
                <Select
                  id="region"
                  labelId="region-label"
                  label={t('edit-profile-modal.region')}
                  name="region"
                  value={formik.values.region}
                  onChange={formik.handleChange}
                  error={formik.touched.region && Boolean(formik.errors.region)}
                  disabled={loading}
                  fullWidth
                >
                  {regions
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map(({ key, label }) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            <FormControl
              required
              error={formik.touched.news && Boolean(formik.errors.news)}
              component="fieldset"
              variant="standard"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    id="news"
                    name="news"
                    checked={formik.values.news}
                    onChange={(_, checked) =>
                      formik.setFieldValue('news', checked)
                    }
                  />
                }
                label={t('authentication:register.news')}
              />
            </FormControl>
          </Container>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            type="submit"
            loading={loading}
            color="primary"
            disabled={loading}
          >
            {t('edit-profile-modal.save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfileChangeModal;
